import { useContext, useEffect, useState } from "react";
import { ConfirmDialog } from "../../../../components/shared";
import { useFormikContext } from "formik";
import { AssignGroupContext } from "./context";
import styled from "styled-components";
import SuggesterField from "../../../../components/shared/suggester/SuggesterField";
import { EquipmentSearchMeta } from "../../../../constants";
import { useSelector, useDispatch } from "react-redux";
import { OwcButton, OwcEditableDropdown, OwcListItem } from "@one/react";
import { withApollo } from "react-apollo";
import { fetchFilteredInstruments } from "../../redux/actions";

export const CreateFieldStyled = styled.div`
  display: flex;
  justify-content: left;
  padding-top: 8px;
  padding-bottom: 32px;
  margin-bottom: 20px;
  & > .selectBox .MuiFilledInput-root {
    height: 48px;
  }
  & > .selectBox,
  .TextFieldInput {
    margin: 0;
    margin-right: 20px;
    width: 50%;
    max-width: 100%;
  }
  & > .searchTextMultiSelect {
    width: 50%;
  }
`;

const GroupDialog = ({ client, onCancel, submit, groupList, selectedEquipment, setSelectedEquipment }) => {
  const formik = useFormikContext();
  const dispatch = useDispatch();
  const { dialogOpen, isEditMode, lastEditGroup } = useContext(AssignGroupContext);

  const bookingInstruments = useSelector((state) => state.bookingInstruments.bookingInstruments);
  const user = useSelector((state) => state.user);
  const { instruments: filteredInstruments, loading: isLoadingInstruments } = useSelector(
    (state) => state.bookingAdmin.filteredInstruments
  );

  const filteredGroupList = groupList?.filter(group => group.id)
  const sortedGroupList = filteredGroupList?.sort((a, b) => a.groupName.localeCompare(b.groupName));

  const itmesOnSearchFieldFilter = async (items, userInput) => {
    if (isEditMode) return [];
    if (!userInput || userInput.trim() === "") return [];

    try {
      const filters = {
        serialNumber: userInput.trim(),
        equipmentId: userInput.trim(),
      };

      // Dispatch Redux action to fetch instruments and wait for it to complete
      await dispatch(fetchFilteredInstruments({
        client,
        user,
        filters,
      }));

      // Note: The actual results will come from Redux state via useSelector
      // This function needs to return a promise that resolves after Redux updates
      // We return an empty array here because the results are managed by Redux
      return [];
    } catch (error) {
      console.error('Error in itmesOnSearchFieldFilter:', error);
      return [];
    }
  };

  // Use useEffect to watch filteredInstruments and return them for the suggester
  const [searchResults, setSearchResults] = useState([]);

  useEffect(() => {
    if (filteredInstruments && filteredInstruments.length > 0 && !isLoadingInstruments) {
      const sortedItems = [...filteredInstruments].sort((a, b) => {
        const nameA = a.equipmentNickName?.toLowerCase() || "";
        const nameB = b.equipmentNickName?.toLowerCase() || "";
        return nameA.localeCompare(nameB);
      });

      setSearchResults(sortedItems);
    } else if (!isLoadingInstruments) {
      setSearchResults([]);
    }
  }, [filteredInstruments, isLoadingInstruments]);

  const selectSuggestionFn = (instrument) => {
    setSelectedEquipment(instrument);
    formik.setValues({
      ...formik.values,
      equipment:
        instrument?.equipmentId && instrument?.equipmentId !== "null"
          ? instrument?.equipmentId
          : instrument.serialNumber,
    });
  };
  useEffect(() => {
    if (isEditMode && lastEditGroup) {
      if ((!formik.values.equipment || formik.values.equipment === "null") && lastEditGroup.equipment) {
        formik.setFieldValue("equipment", lastEditGroup.equipment.serialNumber, false);
      }
      if ((!formik.values.group || formik.values.group === "null") && lastEditGroup.groupName) {
        formik.setFieldValue("group", lastEditGroup.groupName, false);
      }

      if (setSelectedEquipment && (lastEditGroup.equipment?.serialNumber || lastEditGroup.equipment?.equipmentId)) {
        const match = bookingInstruments.find(
          (x) => x.serialNumber === lastEditGroup.equipment.serialNumber || x.equipmentId === lastEditGroup.equipment.equipmentId
        );
        if (match) {
          setSelectedEquipment(match);
        }
      }
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode, formik.values, lastEditGroup]);

  return (
    <ConfirmDialog
      open={dialogOpen}
      close={onCancel}
      approveText={isEditMode ? "Update" : "Assign"}
      approveColor="primary"
      approveVariant="contained"
      cancelText="Cancel"
      cancelVariant="outlined"
      cancelColor="primary"
      onCancel={onCancel}
      isDivider
      maxWidth="sm"
      fullWidth
      testid={"import-file-dialog"}
      title={isEditMode ? "Edit assign group to equipment" : "Assign group to equipment"}
      renderActions={({ cancelColor, cancelVariant, approveColor, approveVariant, cancelText, approveText }) => (
        <div style={{ display: "flex", marginLeft: "auto" }}>
          <OwcButton
            data-testid="confirm-dialog-actions-button-cancel"
            onClick={onCancel}
            variant="secondary"
            style={{ marginRight: 32 }}
          >
            {cancelText}
          </OwcButton>
          <OwcButton
            data-testid="confirm-dialog-actions-button-approve"
            onClick={() => {
              submit(formik.values);
            }}
            variant="primary"
            disabled={formik.values?.equipment === "" || formik.values?.group === "" || formik.values?.group === undefined || !selectedEquipment}
          >
            {approveText}
          </OwcButton>
        </div>
      )}
    >
      <CreateFieldStyled>
        <OwcEditableDropdown
          required={true}
          style={{ width: 250, marginRight: "20px" }}
          name="group"
          label="Select booking group name"
          value={formik.values?.group || null}
          key={formik.values?.group + filteredGroupList?.length}
          data-testid="booking-filter-admin-reserved-for-email"
          onSelectChange={(e) => {
            let selected = "";
            if (e.detail !== null) {
              selected = filteredGroupList.find((x, index) => index === e.detail);
            }
            formik.setFieldValue("group", selected?.groupName, true);
          }}
        >
          {sortedGroupList?.map((item, index) => {
            const option = item.groupName;
            return (
              <>
                <OwcListItem value={option} key={option}>
                  {option}
                </OwcListItem>
              </>
            );
          })}
        </OwcEditableDropdown>
        <SuggesterField
          meta={EquipmentSearchMeta}
          formik={formik}
          label="Enter serial no or equipment ID"
          name="equipment"
          isDisabled={isEditMode}
          selectSuggestionFn={selectSuggestionFn}
          searchItemList={searchResults}
          itmesOnSearchFieldFilter={itmesOnSearchFieldFilter}
          isEditMode={isEditMode}
        />
      </CreateFieldStyled>
    </ConfirmDialog>
  );
};

export default withApollo(GroupDialog);
